import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ProductModal from '../components/catalog/ProductModal';
import { PRODUCTS } from '../data/products';
import { CATEGORY_LIST } from '../data/categories';
import './Home.css';

const HERO_IMAGES = [
  {
    url: '/assets/images/hero/bath-luxury-1.jpg',
    label: 'Luxury Master Bath',
    tag: 'Exotic Bathroom',
  },
  {
    url: '/assets/images/hero/bath-luxury-2.jpg',
    label: 'Exotic Bathroom Design',
    tag: 'Premium Tile',
  },
  {
    url: '/assets/images/hero/bath-luxury-3.jpg',
    label: 'Crystal Frameless Shower',
    tag: 'Luxury Shower',
  },
  {
    url: '/assets/images/hero/bath-luxury-4.jpg',
    label: 'Frameless Glass Shower',
    tag: 'Premium Bath',
  },
  {
    url: '/assets/images/hero/bath-luxury-5.jpg',
    label: 'Eclipse Onyx Porcelain',
    tag: '12×24 Porcelain',
  },
];

const FEATURED = PRODUCTS.filter((p) => p.featured).slice(0, 6);

const PILLARS = [
  {
    number: '01',
    title: 'Made by Us',
    body: 'Every tile in our catalog is designed and manufactured by Arden Surfaces — giving us full control over quality, consistency, and finish.',
  },
  {
    number: '02',
    title: 'Architectural Scale',
    body: 'From intimate bathrooms to grand commercial lobbies, our manufactured range spans 12×24 to 48×72 large-format slabs.',
  },
  {
    number: '03',
    title: 'Direct to You',
    body: 'No middlemen. We manufacture and deliver direct — ensuring faster lead times, competitive pricing, and dedicated project support.',
  },
];

const STATS = [
  { value: 'Curated',      label: 'Products'           },
  { value: 'Multiple',     label: 'Format Sizes'       },
  { value: 'Completed',    label: 'Projects'           },
  { value: 'Dallas',       label: 'Texas'              },
];

export default function Home() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);
  const navigate = useNavigate();
  const displayCategories = CATEGORY_LIST.filter((c) => c.key !== 'all');

  // Auto-advance hero slideshow every 5 s
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="home-page">
      <Helmet>
        <title>Arden Surfaces | Premium Porcelain &amp; Wood-Look Tile — Dallas, TX</title>
        <meta name="description" content="Premium porcelain and wood-look tile designed and manufactured by Arden Surfaces. Factory-direct to homeowners, contractors, and designers in Dallas, Texas." />
        <meta property="og:title" content="Arden Surfaces | Premium Tile — Dallas, TX" />
        <meta property="og:description" content="Factory-direct premium tile for residential and commercial spaces. Showroom in Garland, TX." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ardensurfaces.com/" />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="lx-hero" aria-label="Hero">
        {/* Slideshow backgrounds */}
        {HERO_IMAGES.map((img, i) => (
          <div
            key={img.url}
            className={`lx-hero__bg${i === heroIdx ? ' lx-hero__bg--active' : ''}`}
            style={{ backgroundImage: `url(${img.url})` }}
            aria-hidden="true"
          />
        ))}
        <div className="lx-hero__veil" />

        {/* Tile tag */}
        <div className="lx-hero__room-tag" aria-live="polite">
          {HERO_IMAGES[heroIdx].tag}
        </div>

        {/* Dot indicators */}
        <div className="lx-hero__dots" aria-label="Slideshow controls">
          {HERO_IMAGES.map((img, i) => (
            <button
              key={i}
              className={`lx-hero__dot${i === heroIdx ? ' lx-hero__dot--active' : ''}`}
              onClick={() => setHeroIdx(i)}
              aria-label={img.label}
            />
          ))}
        </div>

        <div className="lx-hero__content">
          <p className="lx-eyebrow">Arden Surfaces &nbsp;·&nbsp; Dallas, Texas</p>
          <div className="lx-hero__rule" aria-hidden="true" />
          <h1 className="lx-hero__title">
            Where Fine Surfaces<br />Meet Inspired Spaces
          </h1>
          <p className="lx-hero__sub">
            Premium porcelain and wood-look tile, designed and manufactured
            by Arden Surfaces for discerning residential and commercial spaces.
          </p>
          <div className="lx-hero__actions">
            <Link to="/catalog" className="lx-btn lx-btn--gold">
              Explore Collection
            </Link>
          </div>
        </div>

        <div className="lx-hero__scroll" aria-hidden="true">
          <span className="lx-hero__scroll-line" />
          <span className="lx-hero__scroll-text">Scroll</span>
        </div>
      </section>


      {/* ── STATS STRIP ──────────────────────────────────────────────────── */}
      <div className="lx-stats">
        {STATS.map(({ value, label }, i) => (
          <React.Fragment key={label}>
            <div className="lx-stat" data-aos="fade-up" data-aos-delay={i * 80}>
              <span className="lx-stat__value">{value}</span>
              <span className="lx-stat__label">{label}</span>
            </div>
            {i < STATS.length - 1 && <div className="lx-stats__div" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── SIGNATURE COLLECTION ─────────────────────────────────────────── */}
      <section className="lx-section lx-section--dark">
        <div className="lx-inner">
          <div className="lx-section__header" data-aos="fade-up">
            <p className="lx-eyebrow lx-eyebrow--gold lx-eyebrow--lg">Rocagres Collection</p>
            <h2 className="lx-heading lx-heading--light">
              Surfaces of Distinction
            </h2>
            <p className="lx-section__sub lx-section__sub--light">
              Premium porcelain and wood-look tile, designed and manufactured for discerning residential and commercial spaces.
            </p>
          </div>

          <div className="lx-tile-grid">
            {FEATURED.map((product, i) => (
              <div
                key={product.id}
                className="lx-tile-card"
                data-aos="fade-up"
                data-aos-delay={i * 100}
                onClick={() => setSelectedProduct(product)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedProduct(product)}
              >
                <div className="lx-tile-card__img-wrap">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0].url}
                      alt={product.name}
                      className="lx-tile-card__img"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="lx-tile-card__img-fallback"
                      style={{ background: product.patternConfig?.baseColor }}
                    />
                  )}
                  <div className="lx-tile-card__overlay" />
                  <div className="lx-tile-card__info">
                    <span className="lx-tile-card__cat">
                      {product.category === 'woodLook' ? 'Wood Look' : 'Porcelain'}
                    </span>
                    <h3 className="lx-tile-card__name">{product.name}</h3>
                    <span className="lx-tile-card__size">{product.sizes?.[0]}"</span>
                  </div>
                </div>
                <div className="lx-tile-card__hover">
                  <span>View Details</span>
                </div>
              </div>
            ))}
          </div>

          <div className="lx-collection-link">
            <Link to="/catalog" className="lx-text-link lx-text-link--light">
              View Complete Collection
              <span className="lx-text-link__arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── SPLIT: BRAND STORY ───────────────────────────────────────────── */}
      <section className="lx-split">
        <div className="lx-split__image" data-aos="fade-right">
          <img
            src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1200&q=85"
            alt="Luxury interior with premium tile"
          />
        </div>
        <div className="lx-split__content" data-aos="fade-left">
          <p className="lx-eyebrow lx-eyebrow--gold">Our Philosophy</p>
          <div className="lx-split__rule" />
          <h2 className="lx-heading">
            Designed, Manufactured, and Delivered with Precision
          </h2>
          <p className="lx-split__body">
            At Arden Surfaces, we don't just distribute tile — we create it.
            From concept to kiln, every piece in our portfolio is designed and
            manufactured by our expert team in India, then delivered direct to
            our customers in Dallas, Texas. Our collection spans
            precision-engineered porcelain to warmly textured wood-look planks,
            each tile bearing the mark of our craft.
          </p>
          <p className="lx-split__body">
            By partnering with our world-class manufacturing team in India and
            our dedicated distribution team in Dallas, Texas, we deliver
            uncompromising consistency in colour, dimension, and finish —
            direct to architects, designers, contractors, and homeowners.
          </p>
          <Link to="/about" className="lx-btn lx-btn--outline">
            Our Story
          </Link>
        </div>
      </section>

      {/* ── PILLARS ──────────────────────────────────────────────────────── */}
      <section className="lx-section lx-section--cream">
        <div className="lx-inner">
          <div className="lx-section__header" data-aos="fade-up">
            <p className="lx-eyebrow lx-eyebrow--gold">The Arden Standard</p>
            <h2 className="lx-heading">Why Designers Choose Us</h2>
          </div>
          <div className="lx-pillars">
            {PILLARS.map(({ number, title, body }, i) => (
              <div key={number} className="lx-pillar" data-aos="fade-up" data-aos-delay={i * 120}>
                <span className="lx-pillar__num">{number}</span>
                <div className="lx-pillar__rule" />
                <h3 className="lx-pillar__title">{title}</h3>
                <p className="lx-pillar__body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────────────────────────── */}
      <section className="lx-section">
        <div className="lx-inner">
          <div className="lx-section__header" data-aos="fade-up">
            <p className="lx-eyebrow lx-eyebrow--gold">Browse the Collection</p>
            <h2 className="lx-heading">Two Worlds of Surface</h2>
          </div>
          <div className="lx-cat-grid">
            {displayCategories.map(({ key, label, description }, i) => (
              <Link key={key} to={`/catalog?category=${key}`} className="lx-cat-card" data-aos="fade-up" data-aos-delay={i * 120}>
                <div
                  className="lx-cat-card__bg"
                  style={{
                    backgroundImage: `url(${
                      key === 'porcelain'
                        ? 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1000&q=80'
                        : 'https://images.unsplash.com/photo-1541123437800-1bb1317badc2?w=1000&q=80'
                    })`,
                  }}
                />
                <div className="lx-cat-card__veil" />
                <div className="lx-cat-card__content">
                  <p className="lx-cat-card__label">{label}</p>
                  <p className="lx-cat-card__desc">{description}</p>
                  <span className="lx-cat-card__cta">Explore →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* ── PRODUCT MODAL ────────────────────────────────────────────────── */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
